import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { initials as nameInitials } from "../utils/initials";
import { useUserDocAccess, type AccessRule } from "../hooks/useDocAccess";
import {
  ArrowLeft, ChevronRight, Building2, Search,
  X, Loader2, Save, RotateCcw, Folder, FileText, Code,
  Check, Minus, ChevronDown, LifeBuoy, HelpCircle,
} from "lucide-react";
import { ConfirmDialog } from "./ConfirmDialog";
import { describePersonReach, supportsUserAccess, type PersonReach } from "./audience";

const BASE = "/studio/settings";

interface ExternalUserFull {
  id: string; email: string; name: string | null;
  avatarUrl: string | null; status: string; createdAt: string;
  partnerMemberships: { partnerId: string; partnerName: string; partnerSlug: string; createdAt: string }[];
}

interface Partner { id: string; name: string; slug: string; }
interface DocSection { id: string; slug: string; title: string; sectionType: string; }
interface TreeNode { id: string; title: string; slug: string; slugPath: string; children?: TreeNode[]; }

/* ── Tree cascade helpers (rule-based, keyed by slugPath) ── */
type CheckState = "checked" | "partial" | "unchecked";
function collectDescendantSlugPaths(node: TreeNode): string[] {
  const paths: string[] = [];
  for (const c of node.children ?? []) { paths.push(c.slugPath); paths.push(...collectDescendantSlugPaths(c)); }
  return paths;
}
function collectAllContentSlugPaths(nodes: TreeNode[]): string[] {
  const paths: string[] = [];
  for (const n of nodes) { paths.push(n.slugPath); paths.push(...collectDescendantSlugPaths(n)); }
  return paths;
}

/** Find a node by slugPath in a tree */
function findNodeBySlugPath(nodes: TreeNode[], slugPath: string): TreeNode | null {
  for (const n of nodes) {
    if (n.slugPath === slugPath) return n;
    if (n.children?.length) {
      const found = findNodeBySlugPath(n.children, slugPath);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Uncheck a node: if it has its own rule, remove it + descendants.
 * If it's covered by an ancestor's include_children, "split" the ancestor —
 * remove ancestor rule, add include_children for siblings at each level down.
 */
function computeUncheck(
  slugPath: string,
  rules: AccessRule[],
  parentMap: Map<string, string | null>,
  allTrees: TreeNode[],
): AccessRule[] {
  const ruleMap = buildRuleMap(rules);
  const nodeInTree = findNodeBySlugPath(allTrees, slugPath);
  const descPaths = nodeInTree ? new Set(collectDescendantSlugPaths(nodeInTree)) : new Set<string>();
  let next = rules.filter(r => r.slug !== slugPath && !descPaths.has(r.slug));

  let ancestorPath: string | null = null;
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    if (ruleMap.get(pid)?.includeChildren) { ancestorPath = pid; break; }
    pid = parentMap.get(pid) ?? null;
  }

  if (!ancestorPath) return next;

  next = next.filter(r => r.slug !== ancestorPath);

  let currentPath = ancestorPath;
  while (currentPath !== slugPath) {
    const currentNode = findNodeBySlugPath(allTrees, currentPath);
    if (!currentNode?.children?.length) break;

    let pathChildPath: string | null = null;
    for (const child of currentNode.children) {
      if (child.slugPath === slugPath || findNodeBySlugPath(child.children ?? [], slugPath)) {
        pathChildPath = child.slugPath;
        break;
      }
    }
    if (!pathChildPath) break;

    for (const child of currentNode.children) {
      if (child.slugPath !== pathChildPath && !next.some(r => r.slug === child.slugPath)) {
        next.push({ slug: child.slugPath, includeChildren: true });
      }
    }

    if (!next.some(r => r.slug === currentPath)) {
      next.push({ slug: currentPath, includeChildren: false });
    }

    currentPath = pathChildPath;
  }

  return next;
}

/** Build a lookup: slug → rule */
function buildRuleMap(rules: AccessRule[]): Map<string, AccessRule> {
  const m = new Map<string, AccessRule>();
  for (const r of rules) m.set(r.slug, r);
  return m;
}

/** Build parent map from tree (slugPath → parent slugPath) */
function buildParentMap(nodes: TreeNode[], parentSlugPath: string | null, out: Map<string, string | null>) {
  for (const n of nodes) {
    out.set(n.slugPath, parentSlugPath);
    if (n.children?.length) buildParentMap(n.children, n.slugPath, out);
  }
}

/** Check if a node is covered by an ancestor's include_children=true */
function isAncestorCovered(slugPath: string, parentMap: Map<string, string | null>, ruleMap: Map<string, AccessRule>): boolean {
  let pid = parentMap.get(slugPath) ?? null;
  while (pid) {
    const rule = ruleMap.get(pid);
    if (rule?.includeChildren) return true;
    pid = parentMap.get(pid) ?? null;
  }
  return false;
}

/** Determine check state for a node given a combined rule map */
function getNodeCheckState(node: TreeNode, ruleMap: Map<string, AccessRule>, parentMap: Map<string, string | null>): CheckState {
  const rule = ruleMap.get(node.slugPath);
  if (rule?.includeChildren) return "checked";
  if (isAncestorCovered(node.slugPath, parentMap, ruleMap)) return "checked";
  if (rule) {
    if (!node.children?.length) return "checked";
    const allDesc = collectDescendantSlugPaths(node);
    const allChecked = allDesc.every(sp => ruleMap.has(sp) || isAncestorCovered(sp, parentMap, ruleMap));
    return allChecked ? "checked" : "partial";
  }
  if (node.children?.length) {
    const anyChild = node.children.some(c => getNodeCheckState(c, ruleMap, parentMap) !== "unchecked");
    return anyChild ? "partial" : "unchecked";
  }
  return "unchecked";
}



/* ── Inline route wrapper ── */
export function ExternalUserDetailInline({ userId }: { userId: string }) {
  const [user, setUser] = useState<ExternalUserFull | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await authFetch(`/api/admin/external-users/${userId}`);
      if (r.ok) setUser(await r.json());
      setLoading(false);
    })();
  }, [userId]);

  if (loading) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;
  if (!user) return <div className="text-sm text-slate-400 py-8 text-center">User not found</div>;

  return <ExternalUserDetailView user={user} onUserChange={setUser} />;
}

/* ── Modal wrapper — used from ExternalUsersSection ── */
export function ExternalUserDetailModal({ userId, initialTab, inviteMode, onClose }: {
  userId?: string | null; initialTab?: "partners" | "access"; inviteMode?: boolean; onClose: () => void;
}) {
  const [user, setUser] = useState<ExternalUserFull | null>(null);
  const [loading, setLoading] = useState(false);

  /* ── Invite-mode state (single-page: everything local until submit) ── */
  const [inviteEmail, setInviteEmail] = useState("");
  const [invErr, setInvErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [inviteTab, setInviteTab] = useState<"partners" | "access">("partners");

  // Local partner selections (no API calls until submit)
  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<Set<string>>(new Set());
  const [partnerSearch, setPartnerSearch] = useState("");

  // Local access config per project
  const [localAccessMap, setLocalAccessMap] = useState<Record<string, { mode: string; rules: AccessRule[] }>>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValid = emailRegex.test(inviteEmail.trim());

  const isInviteFlow = !!inviteMode;

  // Load partners list for invite mode
  useEffect(() => {
    if (isInviteFlow) {
      (async () => {
        const r = await authFetch("/api/admin/partners");
        if (r.ok) setAllPartners(await r.json());
      })();
    }
  }, [isInviteFlow]);

  // Load user data for existing user mode
  useEffect(() => {
    if (!userId || isInviteFlow) return;
    (async () => {
      setLoading(true);
      const r = await authFetch(`/api/admin/external-users/${userId}`);
      if (r.ok) setUser(await r.json());
      setLoading(false);
    })();
  }, [userId, isInviteFlow]);

  const togglePartner = (pid: string) => {
    setSelectedPartnerIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const filteredPartners = allPartners.filter(p =>
    p.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
    p.slug.toLowerCase().includes(partnerSearch.toLowerCase())
  );

  // Submit invite: single POST with email + partner_ids + doc_access
  const handleSubmit = async () => {
    if (!emailValid) return;
    setSubmitting(true); setInvErr("");
    const docAccess = Object.entries(localAccessMap)
      .filter(([, v]) => v.mode !== "default" || v.rules.length > 0)
      .map(([projectId, v]) => ({
        project_id: projectId,
        mode: v.mode,
        rules: v.rules.map(r => ({
          slug: r.slug,
          include_children: r.includeChildren,
        })),
      }));
    const r = await authFetch("/api/admin/external-users/invites", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: inviteEmail.trim(),
        partner_ids: [...selectedPartnerIds],
        doc_access: docAccess,
      }),
    });
    if (r.ok) { onClose(); }
    else {
      const d = await r.json().catch(() => ({}));
      setInvErr(d.detail || "Failed to send invite");
    }
    setSubmitting(false);
  };

  const headerTitle = isInviteFlow
    ? "Invite External User"
    : loading ? "Loading…" : user ? (user.name || user.email) : "User not found";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] min-h-[60vh] flex flex-col mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-semibold text-slate-800">{headerTitle}</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><X className="size-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isInviteFlow ? (
            <>
              {/* Email input */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Email</label>
                <input type="email" value={inviteEmail} onChange={e => { setInviteEmail(e.target.value); setInvErr(""); }}
                  placeholder="user@example.com" autoFocus
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                {invErr && <div className="text-xs text-red-600 mt-1">{invErr}</div>}
              </div>

              {/* Tabs: Partners / Doc Access */}
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
                <button onClick={() => setInviteTab("partners")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inviteTab === "partners" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  Partners {selectedPartnerIds.size > 0 && <span className="text-slate-400 ml-1">{selectedPartnerIds.size}</span>}
                </button>
                <button onClick={() => setInviteTab("access")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${inviteTab === "access" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                  Doc Access
                </button>
              </div>

              {inviteTab === "partners" && (
                <div className="space-y-2">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Partner Memberships</span>
                  {allPartners.length > 5 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
                      <Search className="size-3.5 text-slate-400 shrink-0" />
                      <input type="text" value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} placeholder="Search partners…"
                        className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" />
                    </div>
                  )}
                  {allPartners.length === 0 ? (
                    <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-4 text-center">
                      <Building2 className="size-5 text-slate-300 mx-auto mb-1" />
                      <p className="text-xs text-slate-400">No partners configured yet.</p>
                    </div>
                  ) : (
                    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
                      {filteredPartners.map(p => {
                        const assigned = selectedPartnerIds.has(p.id);
                        return (
                          <label key={p.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${assigned ? "bg-indigo-50/40" : "hover:bg-slate-50"}`}>
                            <input type="checkbox" checked={assigned} onChange={() => togglePartner(p.id)} className="accent-indigo-600 rounded shrink-0" />
                            <div className="size-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0"><Building2 className="size-3.5" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-700">{p.name}</div>
                              <div className="text-[11px] text-slate-400">{p.slug}</div>
                            </div>
                            {assigned && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 shrink-0">selected</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {inviteTab === "access" && (
                <LocalAccessConfig localAccessMap={localAccessMap} onAccessChange={setLocalAccessMap} partnerIds={selectedPartnerIds} />
              )}
            </>
          ) : (
            <>
              {loading && <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>}
              {!loading && userId && !user && <div className="text-sm text-slate-400 py-8 text-center">User not found</div>}
              {!loading && user && (
                <ExternalUserDetailView user={user} onUserChange={setUser} initialTab={initialTab || "partners"} embedded />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {isInviteFlow && (
          <div className="px-5 py-3 border-t border-slate-100 flex justify-between items-center shrink-0">
            <p className="text-[11px] text-slate-400">
              {selectedPartnerIds.size > 0 ? `${selectedPartnerIds.size} partner${selectedPartnerIds.size !== 1 ? "s" : ""} selected` : ""}
            </p>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSubmit} disabled={!emailValid || submitting}
                className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {submitting ? "Sending…" : "Send Invite"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main detail view ── */
function ExternalUserDetailView({ user, onUserChange, initialTab, embedded }: { user: ExternalUserFull; onUserChange: (u: ExternalUserFull) => void; initialTab?: "partners" | "access"; embedded?: boolean }) {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<"partners" | "access">(initialTab || (searchParams.get("tab") as "partners" | "access") || "partners");
  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [toggling, setToggling] = useState<string | null>(null);
  const [partnerSearch, setPartnerSearch] = useState("");

  useEffect(() => {
    (async () => {
      const r = await authFetch("/api/admin/partners");
      if (r.ok) setAllPartners(await r.json());
    })();
  }, []);

  const assignedPartnerIds = new Set(user.partnerMemberships.map(m => m.partnerId));

  const togglePartner = async (partnerId: string) => {
    setToggling(partnerId);
    if (assignedPartnerIds.has(partnerId)) {
      const r = await authFetch(`/api/admin/external-users/${user.id}/partners/${partnerId}`, { method: "DELETE" });
      if (r.ok) {
        const ur = await authFetch(`/api/admin/external-users/${user.id}`);
        if (ur.ok) onUserChange(await ur.json());
      }
    } else {
      const r = await authFetch(`/api/admin/external-users/${user.id}/partners`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partner_id: partnerId }),
      });
      if (r.ok) {
        const ur = await authFetch(`/api/admin/external-users/${user.id}`);
        if (ur.ok) onUserChange(await ur.json());
      }
    }
    setToggling(null);
  };

  const filteredPartners = allPartners.filter(p =>
    p.name.toLowerCase().includes(partnerSearch.toLowerCase()) ||
    p.slug.toLowerCase().includes(partnerSearch.toLowerCase())
  );

  const initials = nameInitials(user.name, user.email);

  return (
    <div className="space-y-4">
      {/* Breadcrumb + profile card — hidden when embedded in modal */}
      {!embedded && (
        <>
          <div className="flex items-center gap-2 text-sm">
            <a href={`${BASE}/external-users`} className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 transition-colors font-medium">
              <ArrowLeft className="size-3.5" />External Users
            </a>
            <ChevronRight className="size-3 text-slate-300" />
            <div className="flex items-center gap-2 min-w-0">
              <div className="size-6 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[10px] font-semibold shrink-0">{initials}</div>
              <span className="text-slate-800 font-semibold truncate">{user.name || user.email}</span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center gap-4">
            <div className="size-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-semibold">{initials}</div>
            <div>
              <div className="text-[15px] font-semibold text-slate-800">{user.name || user.email}</div>
              {user.name && <div className="text-sm text-slate-400">{user.email}</div>}
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${user.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{user.status}</span>
                <span className="text-[11px] text-slate-400">{user.partnerMemberships.length} partner{user.partnerMemberships.length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button onClick={() => setTab("partners")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "partners" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Partners {user.partnerMemberships.length > 0 && <span className="text-slate-400 ml-1">{user.partnerMemberships.length}</span>}
        </button>
        <button onClick={() => setTab("access")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "access" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
          Doc Access
        </button>
      </div>

      {tab === "partners" && (
        <div className="space-y-3">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Partner Memberships</span>
          {allPartners.length > 5 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
              <Search className="size-3.5 text-slate-400 shrink-0" />
              <input type="text" value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} placeholder="Search partners…"
                className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none" />
            </div>
          )}
          {allPartners.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
              <Building2 className="size-5 text-slate-300 mx-auto mb-1.5" />
              <p className="text-xs text-slate-400">No partners configured yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
              {filteredPartners.map(p => {
                const assigned = assignedPartnerIds.has(p.id);
                const busy = toggling === p.id;
                return (
                  <label key={p.id} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${assigned ? "bg-indigo-50/40" : "hover:bg-slate-50"} ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                    <input type="checkbox" checked={assigned} onChange={() => togglePartner(p.id)} disabled={busy}
                      className="accent-indigo-600 rounded shrink-0" />
                    <div className="size-7 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0"><Building2 className="size-3.5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-700">{p.name}</div>
                      <div className="text-[11px] text-slate-400">{p.slug}</div>
                    </div>
                    {assigned && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 shrink-0">assigned</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "access" && <UserAccessConfig userId={user.id} partnerIds={assignedPartnerIds} />}
    </div>
  );
}

/* ── User Access Config — list of projects, click → modal ── */
// `partnerIds` is the set of partners this person belongs to. A
// partner-locked project only takes grants for its own partner's people, so
// without it the screen offered projects the API then 422s on save.
function UserAccessConfig({ userId, partnerIds }: {
  userId: string; partnerIds: Set<string>;
}) {
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [openProject, setOpenProject] = useState<{ id: string; slug: string; name: string } | null>(null);
  // The resolver's answer per project (person_project_reach). null until it
  // arrives — "can't read" for everything in the meantime would be a lie.
  const [reach, setReach] = useState<{ pending: boolean; projects: Record<string, PersonReach> } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch("/api/projects");
      if (res.ok) setAllProjects(await res.json());
      setLoadingProjects(false);
    })();
  }, []);

  // Filtered at render: partnerIds follows the user's memberships, which the
  // sibling tab edits. In the effect (deps []) it was right only because
  // switching tabs remounts this.
  //
  // Grantee-aware. supportsNamedExternalAccess alone offered a partner-locked
  // project to the wrong partner, and the API then 422s on save — the admin
  // picks a project, ticks subtrees and only then finds out.
  const projects = allProjects.filter((p: any) => supportsUserAccess(p, partnerIds));

  const reloadReach = useCallback(async () => {
    const r = await authFetch(`/api/admin/users/${userId}/doc-access-reach`);
    setReach(r.ok ? await r.json() : { pending: false, projects: {} });
  }, [userId]);
  // partnerIds too: adding or removing a partner on the sibling tab changes
  // what they can read.
  const partnerKey = [...partnerIds].sort().join(",");
  useEffect(() => { if (userId) reloadReach(); }, [userId, reloadReach, partnerKey]);

  if (loadingProjects || !reach) return <div className="py-4 text-center text-xs text-slate-400">Loading…</div>;
  if (!projects.length) return <div className="py-4 text-center text-xs text-slate-400">No external projects found.</div>;

  const canRead = projects.filter(p => reach.projects[p.id]?.canRead);
  const cannot = projects.filter(p => !reach.projects[p.id]?.canRead);

  const row = (p: any) => {
    const r = reach.projects[p.id];
    return (
      <button key={p.id} onClick={() => setOpenProject(p)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
          openProject?.id === p.id ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300 bg-white"
        }`}>
        {r?.canRead
          ? <Check className="size-4 text-emerald-600 shrink-0" aria-label="Can read" />
          : <X className="size-4 text-slate-300 shrink-0" aria-label="Can't read" />}
        <div className="flex-1 min-w-0">
          <div className={`text-[13px] font-medium ${r?.canRead ? "text-slate-800" : "text-slate-500"}`}>{p.name}</div>
          <div className="text-[11px] text-slate-500">{describePersonReach(r)}</div>
        </div>
        <span className="text-[11px] text-indigo-600 shrink-0">{r?.canRead ? "Change" : "Give access"}</span>
        <ChevronRight className="size-3.5 text-slate-400 shrink-0" />
      </button>
    );
  };

  return (
    <div className="space-y-4">
      {reach.pending && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Pending invite: this is what they will read once they accept.
        </p>
      )}
      <section aria-label="Can read">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can read ({canRead.length})</h3>
        {canRead.length
          ? <div className="space-y-1">{canRead.map(row)}</div>
          : <p className="text-xs text-slate-400 px-1">Nothing yet. Pick a project below to give access.</p>}
      </section>
      {cannot.length > 0 && (
        <section aria-label="Can't read">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Can't read ({cannot.length})</h3>
          <div className="space-y-1">{cannot.map(row)}</div>
        </section>
      )}
      {openProject && (
        // No onRuleCountChange: given an inline arrow, the modal's effect on
        // it re-rendered this list in a loop. The list reloads on close.
        <UserProjectModal key={openProject.id} userId={userId} project={openProject}
          onClose={() => { setOpenProject(null); reloadReach(); }} />
      )}
    </div>
  );
}

/* ── User project modal — override-only, pre-filled from partner defaults ── */
function UserProjectModal({ userId, project, onClose, onRuleCountChange }: {
  userId: string; project: { id: string; slug: string; name: string }; onClose: () => void;
  onRuleCountChange?: (mode: string, count: number) => void;
}) {
  const [sections, setSections] = useState<DocSection[]>([]);
  const [docTrees, setDocTrees] = useState<Record<string, TreeNode[]>>({});
  const [localRules, setLocalRules] = useState<AccessRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [resetDlg, setResetDlg] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const { config, loading, load, save, clear } = useUserDocAccess(userId, project.id);

  useEffect(() => { load(); }, [load]);

  // Pre-fill: if user has override rules use those, otherwise seed from default rules
  useEffect(() => {
    if (config.mode === "override" && config.rules.length > 0) {
      setLocalRules(config.rules);
      setDirty(false);
    } else {
      setLocalRules(config.defaultRules);
      // Pre-filled from default rules but no override saved yet — mark dirty so Save is active
      setDirty(config.mode === "default" && config.defaultRules.length > 0);
    }
  }, [config]);

  useEffect(() => { onRuleCountChange?.("override", localRules.length); }, [localRules, onRuleCountChange]);

  useEffect(() => {
    (async () => {
      setContentLoading(true);
      const secRes = await authFetch(`/api/docs/${project.slug}/sections`);
      if (!secRes.ok) { setContentLoading(false); return; }
      const secs: DocSection[] = await secRes.json();
      setSections(secs);
      const trees: Record<string, TreeNode[]> = {};
      const treeRes = await authFetch(`/api/docs/${project.slug}/tree`);
      const fullTree = treeRes.ok ? (await treeRes.json()).tree || [] : [];
      for (const s of secs) {
        const sectionNode = fullTree.find((n: any) => n.slug === s.slug);
        trees[s.id] = sectionNode?.children || fullTree;
      }
      setDocTrees(trees);
      setContentLoading(false);
    })();
  }, [project.slug]);

  // Build parent map for all trees
  const parentMap = new Map<string, string | null>();
  for (const sec of sections) {
    parentMap.set(sec.slug, null);
    buildParentMap(docTrees[sec.id] ?? [], sec.slug, parentMap);
  }

  const ruleMap = buildRuleMap(localRules);

  // Flatten all trees for node lookup
  const allTrees: TreeNode[] = [];
  for (const sec of sections) {
    allTrees.push({ id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] });
  }

  const toggleSection = (sec: DocSection) => {
    setDirty(true);
    const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
    const state = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(sec.slug, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(sectionAsNode));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== sec.slug);
        next.push({ slug: sec.slug, includeChildren: true });
        return next;
      });
    }
  };

  const toggleNode = (slugPath: string, node: TreeNode) => {
    setDirty(true);
    const state = getNodeCheckState(node, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(slugPath, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(node));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== slugPath);
        next.push({ slug: slugPath, includeChildren: true });
        return next;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true); setSaveMsg(null);
    const ok = await save("override", localRules);
    setSaving(false); setDirty(false);
    setSaveMsg(ok ? { ok: true, text: "User access rules saved" } : { ok: false, text: "Failed to save" });
    setTimeout(() => setSaveMsg(null), 3000);
  };
  const handleReset = () => setResetDlg(true);
  const doReset = async () => { setResetDlg(false); await clear(); setDirty(false); };

  const isLoading = loading || contentLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col relative" onClick={e => e.stopPropagation()}>
        {saveMsg && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[9999]">
            <div className={`flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border text-sm font-medium ${
              saveMsg.ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-red-50 border-red-300 text-red-800"
            }`}>
              {saveMsg.ok ? <Check className="size-5 text-emerald-600" /> : <X className="size-5 text-red-600" />}
              {saveMsg.text}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100" title="Back to projects">
              <ArrowLeft className="size-4" />
            </button>
            <div>
              <div className="text-sm font-semibold text-slate-800">{project.name}</div>
              <div className="text-[12px] text-slate-400">User override</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving || !dirty}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] rounded-lg transition-colors ${
                dirty ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-100 text-slate-400 cursor-default"
              }`}>
              {saving ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />} Save
            </button>
            <button onClick={handleReset} disabled={config.mode === "default"}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] rounded-lg transition-colors ${
                config.mode !== "default" ? "bg-red-600 text-white hover:bg-red-700" : "bg-slate-100 text-slate-400 cursor-default"
              }`}>
              <RotateCcw className="size-3" /> Reset
            </button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
              <X className="size-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="py-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <Loader2 className="size-3 animate-spin" /> Loading…
            </div>
          ) : sections.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No content found for this project.</div>
          ) : (
            <div className="space-y-3">
              {sections.map(sec => {
                const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
                const secState = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
                return (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 mb-1">
                      <UAccessCheckbox state={secState} locked={false} onChange={() => toggleSection(sec)} />
                      {sec.sectionType === "api"
                        ? <Code className="size-3.5 text-indigo-500" />
                        : sec.sectionType === "support"
                          ? <LifeBuoy className="size-3.5 text-sky-500" />
                          : sec.sectionType === "faq"
                            ? <HelpCircle className="size-3.5 text-violet-500" />
                            : <FileText className="size-3.5 text-slate-500" />}
                      <span className="text-[12px] font-semibold text-slate-700">{sec.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{sec.sectionType}</span>
                    </div>
                    {docTrees[sec.id]?.map(n => (
                      <UAccessDocNode key={n.id} node={n} depth={1} readonly={false}
                        localMode="override" ruleMap={ruleMap} parentMap={parentMap}
                        isPartnerCovered={() => false} toggleNode={toggleNode} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-2.5 border-t border-slate-200 text-[11px] text-slate-400 shrink-0">
          {localRules.length} rule{localRules.length !== 1 ? "s" : ""}
        </div>
      </div>
      {resetDlg && (
        <ConfirmDialog title="Reset access rules?" message="This will remove the override and fall back to the project's External view."
          confirmLabel="Reset" destructive onConfirm={doReset} onCancel={() => setResetDlg(false)} />
      )}
    </div>
  );
}


/* ── Checkbox with locked/readonly state + indeterminate ── */
function UAccessCheckbox({ state, locked, onChange }: { state: CheckState; locked: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} disabled={locked}
      className={`size-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        state === "checked"
          ? locked ? "bg-slate-400 border-slate-400 text-white cursor-default" : "bg-indigo-600 border-indigo-600 text-white"
          : state === "partial"
          ? locked ? "bg-slate-400 border-slate-400 text-white cursor-default" : "bg-indigo-600 border-indigo-600 text-white"
          : locked ? "border-slate-200 cursor-default" : "border-slate-300 hover:border-slate-400"
      }`}>
      {state === "checked" && <Check className="size-2.5" />}
      {state === "partial" && <Minus className="size-2.5" />}
    </button>
  );
}

/* ── Doc node row ── */
function UAccessDocNode({ node, depth, readonly, localMode, ruleMap, parentMap, isPartnerCovered, toggleNode }: {
  node: TreeNode; depth: number; readonly: boolean; localMode: string;
  ruleMap: Map<string, AccessRule>;
  parentMap: Map<string, string | null>;
  isPartnerCovered: (id: string) => boolean;
  toggleNode: (slugPath: string, node: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const nodeIsPartner = isPartnerCovered(node.slugPath);
  const locked = readonly || (localMode === "default_plus" && nodeIsPartner);
  const state = getNodeCheckState(node, ruleMap, parentMap);

  return (
    <div>
      <div className={`flex items-center gap-1.5 py-0.5 rounded ${locked ? "" : "hover:bg-slate-50"}`}
        style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : <span className="w-4" />}
        <UAccessCheckbox state={state} locked={locked} onChange={() => !locked && toggleNode(node.slugPath, node)} />
        {hasChildren ? <Folder className="size-3 text-amber-500" /> : <FileText className="size-3 text-slate-400" />}
        <span className={`text-[12px] truncate ${locked && state !== "unchecked" ? "text-slate-400" : "text-slate-700"}`}>{node.title}</span>
        {locked && nodeIsPartner && !readonly && (
          <span className="ml-auto text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-400">from partner</span>
        )}
      </div>
      {expanded && hasChildren && node.children!.map(c => (
        <UAccessDocNode key={c.id} node={c} depth={depth + 1} readonly={readonly}
          localMode={localMode} ruleMap={ruleMap} parentMap={parentMap}
          isPartnerCovered={isPartnerCovered} toggleNode={toggleNode} />
      ))}
    </div>
  );
}


/* ── Local Access Config — used in invite/create flows (no API calls) ── */
// The invite-time variant. The invitee's partners are chosen in the same
// form, so they arrive as a prop; an empty set excludes every partner-locked
// project, which is what the API would do on save.
function LocalAccessConfig({ localAccessMap, onAccessChange, partnerIds }: {
  localAccessMap: Record<string, { mode: string; rules: AccessRule[] }>;
  onAccessChange: (m: Record<string, { mode: string; rules: AccessRule[] }>) => void;
  // Required, not optional: an optional prop with a deny-everything default
  // meant forgetting it silently offered no partner project and raised no TS
  // error. The sibling UserAccessConfig did pass it, which is what made the
  // asymmetry easy to miss.
  partnerIds: Set<string>;
}) {
  const [allProjects, setAllProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [openProject, setOpenProject] = useState<{ id: string; slug: string; name: string } | null>(null);

  useEffect(() => {
    (async () => {
      const res = await authFetch("/api/projects");
      if (res.ok) setAllProjects(await res.json());
      setLoadingProjects(false);
    })();
  }, []);

  // Filtered at render, not in the effect: partnerIds is set on the other
  // tab, and with deps [] the list was right only because switching tabs
  // unmounts this component.
  //
  // Grantee-aware. supportsNamedExternalAccess alone offered a partner-locked
  // project to the wrong partner, and the API then 422s on save — the admin
  // picks a project, ticks subtrees and only then finds out.
  const projects = allProjects.filter((p: any) => supportsUserAccess(p, partnerIds));

  if (loadingProjects) return <div className="py-3 text-center text-xs text-slate-400">Loading projects…</div>;
  if (!projects.length) return <div className="py-3 text-center text-xs text-slate-400">No external projects found.</div>;

  const modeLabel: Record<string, string> = { default: "External view", default_plus: "External view +", override: "Only what you tick" };

  return (
    <div className="space-y-1 mt-2">
      {projects.map(p => {
        const cfg = localAccessMap[p.id];
        const configured = !!cfg && cfg.mode !== "default";
        return (
          <button key={p.id} onClick={() => setOpenProject(p)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors ${
              openProject?.id === p.id ? "border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200" : "border-slate-200 hover:border-slate-300 bg-white"
            }`}>
            <Folder className="size-4 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-slate-800">{p.name}</div>
              <div className="text-[11px] text-slate-400 font-mono">/{p.slug}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {configured ? (
                <>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{modeLabel[cfg.mode] || cfg.mode}</span>
                  {cfg.rules.length > 0 && <span className="text-[10px] text-slate-400">{cfg.rules.length}</span>}
                </>
              ) : (
                <span className="text-[10px] text-slate-300">not configured</span>
              )}
              <ChevronRight className="size-3.5 text-slate-400" />
            </div>
          </button>
        );
      })}
      {openProject && (
        <LocalProjectModal key={openProject.id} project={openProject}
          initialMode={localAccessMap[openProject.id]?.mode || "default"}
          initialRules={localAccessMap[openProject.id]?.rules || []}
          onSave={(mode, rules) => {
            onAccessChange({ ...localAccessMap, [openProject.id]: { mode, rules } });
            setOpenProject(null);
          }}
          onClose={() => setOpenProject(null)} />
      )}
    </div>
  );
}

/* ── Local Project Modal — override-only tree (no API save, returns data to parent) ── */
function LocalProjectModal({ project, initialRules, onSave, onClose }: {
  project: { id: string; slug: string; name: string };
  initialMode?: string; initialRules: AccessRule[];
  onSave: (mode: string, rules: AccessRule[]) => void;
  onClose: () => void;
}) {
  const [sections, setSections] = useState<DocSection[]>([]);
  const [docTrees, setDocTrees] = useState<Record<string, TreeNode[]>>({});
  const [localRules, setLocalRules] = useState<AccessRule[]>(initialRules);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setContentLoading(true);
      const secRes = await authFetch(`/api/docs/${project.slug}/sections`);
      if (!secRes.ok) { setContentLoading(false); return; }
      const secs: DocSection[] = await secRes.json();
      setSections(secs);
      const trees: Record<string, TreeNode[]> = {};
      const treeRes = await authFetch(`/api/docs/${project.slug}/tree`);
      const fullTree = treeRes.ok ? (await treeRes.json()).tree || [] : [];
      for (const s of secs) {
        const sectionNode = fullTree.find((n: any) => n.slug === s.slug);
        trees[s.id] = sectionNode?.children || fullTree;
      }
      setDocTrees(trees);
      setContentLoading(false);
    })();
  }, [project.slug]);

  // Build parent map + rule map for local rules only (no partner context in local/invite flow)
  const parentMap = new Map<string, string | null>();
  for (const sec of sections) {
    parentMap.set(sec.slug, null);
    buildParentMap(docTrees[sec.id] ?? [], sec.slug, parentMap);
  }
  const ruleMap = buildRuleMap(localRules);

  // Flatten all trees for node lookup
  const allTrees: TreeNode[] = [];
  for (const sec of sections) {
    allTrees.push({ id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] });
  }

  const toggleSection = (sec: DocSection) => {
    const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
    const state = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(sec.slug, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectAllContentSlugPaths(docTrees[sec.id] ?? []));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== sec.slug);
        next.push({ slug: sec.slug, includeChildren: true });
        return next;
      });
    }
  };

  const toggleNode = (slugPath: string, node: TreeNode) => {
    const state = getNodeCheckState(node, ruleMap, parentMap);
    if (state === "checked") {
      setLocalRules(prev => computeUncheck(slugPath, prev, parentMap, allTrees));
    } else {
      const descPaths = new Set(collectDescendantSlugPaths(node));
      setLocalRules(prev => {
        const next = prev.filter(r => !descPaths.has(r.slug) && r.slug !== slugPath);
        next.push({ slug: slugPath, includeChildren: true });
        return next;
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] min-h-[50vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100"><ArrowLeft className="size-4" /></button>
            <div>
              <div className="text-sm font-semibold text-slate-800">{project.name}</div>
              <div className="text-[12px] text-slate-400">Configure doc access</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="size-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {contentLoading ? (
            <div className="py-8 flex items-center justify-center gap-1.5 text-xs text-slate-400"><Loader2 className="size-3 animate-spin" /> Loading…</div>
          ) : sections.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No content found for this project.</div>
          ) : (
            <div className="space-y-3">
              {sections.map(sec => {
                const sectionAsNode: TreeNode = { id: sec.id, title: sec.title, slug: sec.slug, slugPath: sec.slug, children: docTrees[sec.id] ?? [] };
                const secState = getNodeCheckState(sectionAsNode, ruleMap, parentMap);
                return (
                  <div key={sec.id}>
                    <div className="flex items-center gap-2 mb-1">
                      <UAccessCheckbox state={secState} locked={false} onChange={() => toggleSection(sec)} />
                      {sec.sectionType === "api"
                        ? <Code className="size-3.5 text-indigo-500" />
                        : sec.sectionType === "support"
                          ? <LifeBuoy className="size-3.5 text-sky-500" />
                          : sec.sectionType === "faq"
                            ? <HelpCircle className="size-3.5 text-violet-500" />
                            : <FileText className="size-3.5 text-slate-500" />}
                      <span className="text-[12px] font-semibold text-slate-700">{sec.title}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{sec.sectionType}</span>
                    </div>
                    {docTrees[sec.id]?.map(n => (
                      <LocalDocNode key={n.id} node={n} depth={1} readonly={false} ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between shrink-0">
          <span className="text-[11px] text-slate-400">{localRules.length} rule{localRules.length !== 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
            <button onClick={() => onSave("override", localRules)}
              className="px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Local doc node (simplified — no partner defaults) ── */
function LocalDocNode({ node, depth, readonly, ruleMap, parentMap, toggleNode }: {
  node: TreeNode; depth: number; readonly: boolean;
  ruleMap: Map<string, AccessRule>;
  parentMap: Map<string, string | null>;
  toggleNode: (slugPath: string, node: TreeNode) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = (node.children?.length ?? 0) > 0;
  const state = getNodeCheckState(node, ruleMap, parentMap);

  return (
    <div>
      <div className={`flex items-center gap-1.5 py-0.5 rounded ${readonly ? "" : "hover:bg-slate-50"}`}
        style={{ paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button onClick={() => setExpanded(!expanded)} className="p-0.5 text-slate-400 hover:text-slate-600">
            {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
          </button>
        ) : <span className="w-4" />}
        <UAccessCheckbox state={state} locked={readonly} onChange={() => !readonly && toggleNode(node.slugPath, node)} />
        {hasChildren ? <Folder className="size-3 text-amber-500" /> : <FileText className="size-3 text-slate-400" />}
        <span className={`text-[12px] truncate text-slate-700`}>{node.title}</span>
      </div>
      {expanded && hasChildren && node.children!.map(c => (
        <LocalDocNode key={c.id} node={c} depth={depth + 1} readonly={readonly} ruleMap={ruleMap} parentMap={parentMap} toggleNode={toggleNode} />
      ))}
    </div>
  );
}
